import React, { useState } from 'react'

import {
  Button,
  Fieldset,
  Input,
  Layout,
  Heading,
} from '@vwfs-bronson/bronson-react'
import {
  phoneCountryCodes,
  countries,
  countriesEU,
} from '../../../resources/base/models'
import { Select } from '../../../components/Select'
import { FormField } from '../../../components/FormField'
import { FormFieldDate } from '../../../components/InputDate/InputDate'
import { PartnerType, LegalFormType } from '../../../models/enums'
import { mobilePhoneNumberSchema } from './validation-schema'
import { ZipCodeAutocomplete } from '../../../components/AddressAutocomplete/ZipCodeAutocomplete'
import { StreetAutocomplete } from '../../../components/AddressAutocomplete/StreetAutocomplete'
import { FormikProps, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { getPartnerType, hasEmployeeDiscount } from '../../../services/common/utils'

const CompanyName = ({ isVtiGiS = false }) => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="companyName"
      type="input"
      name={isVtiGiS ? 'customerData.companyName' : 'companyData.name'}
      labelText={
        isVtiGiS
          ? t('personal-details:companyName:VtiGis')
          : t('personal-details:companyName:text')
      }
      required
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const EntityLegalForm = () => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="entityLegalForm"
      type="select"
      name="companyData.entityLegalForm"
      labelText={t('personal-details:entityLegalForm.title')}
      required
      render={(fieldProps) => (
        <Select {...fieldProps}>
          <Select.Item value={LegalFormType.SOLE_TRADER}>
            {t('personal-details:entityLegalForm.label.SOLE_TRADER')}
          </Select.Item>
          <Select.Item value={LegalFormType.REGISTERED_SOLE_TRADER}>
            {t('personal-details:entityLegalForm.label.REGISTERED_SOLE_TRADER')}
          </Select.Item>
          <Select.Item value={LegalFormType.FREELANCER}>
            {t('personal-details:entityLegalForm.label.FREELANCER')}
          </Select.Item>
        </Select>
      )}
    />
  )
}

const FirstName = ({ required = true }) => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="firstName"
      type="input"
      name="customerData.firstName"
      labelText={t('personal-details:firstName')}
      autoComplete="given-name"
      required={required}
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const LastName = ({ required = true }) => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="lastName"
      type="input"
      name="customerData.lastName"
      labelText={t('personal-details:lastName')}
      autoComplete="family-name"
      required={required}
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const Street = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()

  const isDisabled = () => {
    return (
      !(values as any)?.address?.[0]?.isManual &&
      !(values as any)?.address?.[0]?.city
    )
  }

  return (
    <FormField
      testId="street"
      type="input"
      name="customerData.address.street"
      labelText={t('personal-details:street')}
      autoComplete="address-line1"
      required
      render={(fieldProps) => <Input {...fieldProps} disabled={isDisabled()} />}
    />
  )
}

const HouseNumber = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()

  const isDisabled = () => {
    return (
      !(values as any)?.address?.[0]?.isManual &&
      !(values as any)?.streetSelected
    )
  }

  return (
    <FormField
      testId="houseNumber"
      type="input"
      name="customerData.address.houseNumber"
      labelText={t('personal-details:houseNumber')}
      autoComplete="address-line2"
      required
      render={(fieldProps) => <Input {...fieldProps} disabled={isDisabled()} />}
    />
  )
}

const ZipCode = () => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="zipCode"
      type="input"
      name="customerData.address.zipCode"
      labelText={t('personal-details:zipCode')}
      autoComplete="postal-code"
      required
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const City = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()
  const isManual = (values as any)?.address?.[0]?.isManual

  return (
    <FormField
      testId="city"
      type="input"
      name="customerData.address.city"
      labelText={t('personal-details:city')}
      autoComplete="address-level2"
      required
      render={(fieldProps) => <Input {...fieldProps} disabled={!isManual} />}
    />
  )
}

const Email = () => {
  const { t } = useTranslation()
  return (
    <FormField
      testId="email"
      type="input"
      name="customerData.contactData.email"
      labelText={t('personal-details:email')}
      infoIcon={t('personal-details:email_tooltip')}
      autoComplete="email"
      required
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const CountryCode = ({ isVtiGis = false }) => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="countryCode"
      type="select"
      name="customerData.address.countryCode"
      labelText={t('personal-details:countryCode')}
      autoComplete="country-name"
      required
      render={(fieldProps) => (
        <Select {...fieldProps}>
          {isVtiGis ? (
            countriesEU?.map((country) => (
              <Select.Item
                key={`${country.value}${country.label}`}
                selected={country.value === fieldProps.value}
                value={country.value}
              >
                {country.label}
              </Select.Item>
            ))
          ) : (
            <Select.Item
              key={`${countries[0].value[0]}${countries[0].label}`}
              selected={countries[0].value === fieldProps.value}
              value={countries[0].value}
            >
              {countries[0].label}
            </Select.Item>
          )}
        </Select>
      )}
    />
  )
}

const MobilePhoneCountryCode = () => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="mobilePhoneCountryCode"
      type="select"
      name="customerData.contactData.mobilePhoneCountryCode"
      labelText={t('personal-details:mobilePhoneCountryCode')}
      autoComplete="tel-country-code"
      required
      render={(fieldProps) => (
        <Select {...fieldProps}>
          {phoneCountryCodes.map(({ value, label }) => (
            <Select.Item key={`${value}${label}`} value={value}>
              {label}
            </Select.Item>
          ))}
        </Select>
      )}
    />
  )
}

const MobilePhoneNumber = ({ values, previousPhone, setPreviousPhone }) => {
  const { t } = useTranslation()

  const validationFunction = async (value) => {
    const mobilePhoneCountryCode =
      values?.customerData?.contactData?.mobilePhoneCountryCode
    try {
      await mobilePhoneNumberSchema.validate({
        mobilePhoneCountryCode,
        mobilePhoneNumber: value,
        previousValue: previousPhone,
      })
      if (mobilePhoneCountryCode && mobilePhoneCountryCode !== 'NONE') {
        // validation is successful (empty error message)
        setPreviousPhone({
          mobilePhoneCountryCode,
          mobilePhoneNumber: value,
          message: '',
        })
      }
      return null
    } catch (e: any) {
      setPreviousPhone({
        mobilePhoneCountryCode,
        mobilePhoneNumber: value,
        message: e?.message,
      })
      return e?.message
    }
  }

  return (
    <FormField
      testId="mobilePhoneNumber"
      type="input"
      name="customerData.contactData.mobilePhoneNumber"
      labelText={t('personal-details:mobilePhoneNumber')}
      validate={validationFunction}
      autoComplete="tel-national"
      required
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const DKXNumber = () => {
  const { t } = useTranslation()
  return (
    <FormField
      testId="dkxNumber"
      type="input"
      name="dkxNumber"
      labelText={t('personal-details:dkxNumber')}
      required
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const DateOfBirth = ({ required = true }) => {
  const { t } = useTranslation()

  return (
    <FormFieldDate
      testId="dateOfBirth"
      name="customerData.dateOfBirth"
      labelText={t('personal-details:dateOfBirth')}
      autoComplete="bday"
      required={required}
    />
  )
}

export const FieldsetBASE = ({ isBusiness }) => {
  const { t } = useTranslation()
  const { values } = useFormikContext() as FormikProps<any>
  const [previousPhone, setPreviousPhone] = useState({
    mobilePhoneCountryCode: '',
    mobilePhoneNumber: '',
    message: '',
  })
  return (
    <Fieldset id="editPersonalData">
      <Fieldset.Row>
        <Layout equalHeight>
          {isBusiness && (
            <>
              {' '}
              <Layout.Item default="1/2" s="1/1">
                <CompanyName />
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <EntityLegalForm />
              </Layout.Item>
            </>
          )}
          <Layout.Item default="1/2" s="1/1">
            <FirstName />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <LastName />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            {values?.address?.[0]?.isManual ? (
              <ZipCode />
            ) : (
              <ZipCodeAutocomplete
                prefixCode={`${values?.customerData?.address.countryCode}`}
                label={t('personal-details:zipCode')}
              />
            )}
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <City />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1" style={{ alignItems: 'baseline' }}>
            {values?.address?.[0]?.isManual ? (
              <Street />
            ) : (
              <StreetAutocomplete
                prefixCode={`${values?.customerData?.address.countryCode}`}
                label={t('personal-details:street')}
              />
            )}
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <HouseNumber />
          </Layout.Item>
          {!isBusiness && (
            <Layout.Item default="1/2" s="1/1">
              <DateOfBirth />
            </Layout.Item>
          )}
          <Layout.Item default="1/2" s="1/1">
            <Email />
          </Layout.Item>
          {isBusiness && <Layout.Item default="1/2" s="1/1"></Layout.Item>}
          <Layout.Item default="1/2" s="1/1">
            <MobilePhoneCountryCode />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <MobilePhoneNumber
              values={values}
              previousPhone={previousPhone}
              setPreviousPhone={setPreviousPhone}
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  )
}

export const FieldsetVTI = () => {
  const { t } = useTranslation()
  const partnerType = getPartnerType()
  const { setFieldValue, values } = useFormikContext() as FormikProps<any>
  const [previousPhone, setPreviousPhone] = useState({
    mobilePhoneCountryCode: '',
    mobilePhoneNumber: '',
    message: '',
  })
  return (
    <Fieldset id="editPersonalData">
      <Fieldset.Row>
        <Layout equalHeight>
          {partnerType === PartnerType.BUSINESS && (
            <>
              <Layout.Item default="1/2" s="1/1">
                <CompanyName isVtiGiS />
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1" />
            </>
          )}
          <Layout.Item default="1/2" s="1/1">
            <FirstName />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <LastName />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <DateOfBirth
              required={partnerType === PartnerType.PRIVATE ? true : false}
            />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <Email />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <MobilePhoneCountryCode />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <MobilePhoneNumber
              values={values}
              previousPhone={previousPhone}
              setPreviousPhone={setPreviousPhone}
            />
          </Layout.Item>
          {hasEmployeeDiscount() && (
            <Layout.Item default="1/2" s="1/1">
              <DKXNumber />
            </Layout.Item>
          )}
          <Layout.Item default="1/1">
            <Heading level={6} className="u-mb-none">
              {t('personal-details:billingAddress')}
            </Heading>
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <CountryCode isVtiGis />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <Button
              className="u-mt"
              type="button"
              link
              disabled={values?.address?.[0]?.isManual}
              ariaDisabled={values?.address?.[0]?.isManual}
              onClick={(e: any) => {
                e.preventDefault()
                setFieldValue(`address[0].isManual`, true)
              }}
            >
              {t('personal-details:manualMode')}
            </Button>
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            {values?.address?.[0]?.isManual ? (
              <ZipCode />
            ) : (
              <ZipCodeAutocomplete
                prefixCode={`${values?.customerData?.address.countryCode}`}
                label={t('personal-details:zipCode')}
              />
            )}
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <City />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1" style={{ alignItems: 'baseline' }}>
            {values?.address?.[0]?.isManual ? (
              <Street />
            ) : (
              <StreetAutocomplete
                prefixCode={`${values?.customerData?.address.countryCode}`}
                label={t('personal-details:street')}
              />
            )}
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">
            <HouseNumber />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  )
}

export const RenderFieldset = ({ isBusiness, isVtiGis }) => {
  return isVtiGis ? <FieldsetVTI /> : <FieldsetBASE isBusiness={isBusiness} />
}
